/**
 * Hex Decode Tool
 *
 * Decode hexadecimal string back to text using browser-native TextDecoder.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const HexDecodeTool: ITransformerTool = {
  id: 'hex-decode',
  name: 'Hex Decode',
  description: 'Decode hexadecimal to text',
  version: '1.0.0',
  type: 'transformer',
  category: 'Encoders & Decoders',
  keywords: ['hex', 'hexadecimal', 'decode', 'hex to text'],

  seo: {
    slug: 'hex-decoder',
    pageTitle: 'Hex Decoder - Free Online Tool | Prism',
    metaDescription: 'Decode hexadecimal strings back to readable text. Auto-detects common separators. Fast, private, runs entirely in your browser.',
    keywords: ['hex decoder', 'hex to text', 'hexadecimal decoder', 'hex decode online', 'hex to string'],
    relatedTools: ['hex-encode', 'base64-decode', 'url-decode'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.HEX, SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [],

  async transform(input: string): Promise<string> {
    const text = String(input ?? '').trim();
    if (!text) return '';

    try {
      // Strip common separators and prefixes: spaces, colons, 0x prefixes, commas
      const cleaned = text
        .replace(/0x/gi, '')
        .replace(/[:\s,]/g, '');

      if (!/^[0-9a-fA-F]*$/.test(cleaned)) {
        throw ToolError.validation('Input contains invalid hexadecimal characters');
      }

      if (cleaned.length % 2 !== 0) {
        throw ToolError.validation('Hex string must have an even number of characters');
      }

      const bytes = new Uint8Array(cleaned.length / 2);
      for (let i = 0; i < cleaned.length; i += 2) {
        bytes[i / 2] = parseInt(cleaned.slice(i, i + 2), 16);
      }

      const decoder = new TextDecoder('utf-8', { fatal: true });
      return decoder.decode(bytes);
    } catch (e) {
      if (e instanceof ToolError) throw e;
      const message = e instanceof Error ? e.message : 'Failed to decode hex';
      throw ToolError.execution(message);
    }
  },
};
